import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Loader2, BellRing, BellOff, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { WaitlistFormValues } from "./schema";
import { PersonalDetailsSection } from "./form-sections/PersonalDetailsSection";
import { LocationPreferencesSection } from "./form-sections/LocationPreferencesSection";
import { CareRequirementsSection } from "./form-sections/CareRequirementsSection";
import { ResidentDetailsSection } from "./form-sections/ResidentDetailsSection";
import { TimelineSection } from "./form-sections/TimelineSection";
import { FinancialSection } from "./form-sections/FinancialSection";
import { AdditionalInformationSection } from "./form-sections/AdditionalInformationSection";
import { PrivacyConsentSection } from "./form-sections/PrivacyConsentSection";
import { useWaitlistForm } from "@/hooks/use-waitlist-form";
import { useWaitlistSubmission } from "@/hooks/use-waitlist-submission";
import { useWaitlistPreferences } from "@/hooks/use-waitlist-preferences";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

interface MultiStepWaitlistFormProps {
  nursingHomeName?: string;
  countyName?: string;
  nursingHomeSlug?: string;
}

const STEPS = [
  {
    title: "Personal & Location",
    description: "Tell us about yourself and where you're looking"
  },
  {
    title: "Care Requirements", 
    description: "Details about care needs and timeline"
  },
  {
    title: "Additional Information",
    description: "Financial preferences and final details"
  }
];

export function MultiStepWaitlistForm({
  nursingHomeName,
  countyName,
  nursingHomeSlug
}: MultiStepWaitlistFormProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(null);

  const {
    form,
    user
  } = useWaitlistForm({
    countyName,
    nursingHomeName
  });

  const {
    isSubmitting,
    submitWaitlistForm,
    handleUnauthenticatedSubmission
  } = useWaitlistSubmission({
    nursingHomeName,
    countyName,
    nursingHomeSlug
  });

  const {
    waitlistActive,
    updateWaitlistStatus,
    savePreferences
  } = useWaitlistPreferences();

  // Auto-save functionality
  useEffect(() => {
    const subscription = form.watch(() => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
      
      const timer = setTimeout(async () => {
        if (user) {
          try {
            await savePreferences(form.getValues());
          } catch (error) {
            console.error("Auto-save failed:", error);
          }
        }
      }, 2000); // Save after 2 seconds of inactivity
      
      setAutoSaveTimer(timer);
    });

    return () => {
      subscription.unsubscribe();
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [form, user, savePreferences, autoSaveTimer]);

  // Validate current step
  const validateStep = (stepIndex: number): boolean => {
    const values = form.getValues();
    
    switch (stepIndex) {
      case 0: // Personal & Location
        return !!(values.firstName && values.lastName && values.email);
      case 1: // Care Requirements
        return !!(values.residentRelation && values.timeframe);
      case 2: // Additional Information
        return values.consentToPrivacyPolicy;
      default:
        return true;
    }
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      toast.error("Please complete all required fields before proceeding");
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  async function onSubmit(data: WaitlistFormValues) {
    if (!validateStep(2)) {
      toast.error("Please complete all required fields");
      return;
    }

    if (!user) {
      handleUnauthenticatedSubmission(data);
      return;
    }

    // Save preferences before submission
    await savePreferences(data);
    
    const success = await submitWaitlistForm(data, user.id);
    if (success) {
      form.reset();
      setCurrentStep(0);
      setCompletedSteps([]);
    }
  }

  const handleWaitlistToggle = (checked: boolean) => {
    updateWaitlistStatus(checked);
  };

  const progressPercentage = ((currentStep + 1) / STEPS.length) * 100;

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <>
            <PersonalDetailsSection control={form.control} />
            <LocationPreferencesSection 
              control={form.control} 
              preselectedNursingHomeSlug={nursingHomeSlug}
              preselectedCounty={countyName ? countyName.toLowerCase().replace(/\s+/g, '-') : undefined}
            />
          </>
        );
      case 1:
        return (
          <>
            <CareRequirementsSection control={form.control} />
            <ResidentDetailsSection control={form.control} />
            <TimelineSection control={form.control} />
          </>
        );
      case 2:
        return (
          <>
            <FinancialSection control={form.control} />
            <AdditionalInformationSection control={form.control} />
            <PrivacyConsentSection control={form.control} />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Header */}
      <Card className="mb-6">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between mb-4">
            <CardTitle className="text-2xl">Join Our Waitlist</CardTitle>
            {user && (
              <div className="text-sm text-gray-500">
                Auto-save enabled
              </div>
            )}
          </div>
          
          <div className="space-y-4">
            <Progress value={progressPercentage} className="h-2" />
            
            <div className="flex justify-between">
              {STEPS.map((step, index) => (
                <div 
                  key={index}
                  className={`flex items-center space-x-2 text-sm ${
                    index === currentStep 
                      ? 'text-grandpal-green font-medium' 
                      : completedSteps.includes(index)
                        ? 'text-green-600'
                        : 'text-gray-500'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                    index === currentStep
                      ? 'border-grandpal-green bg-grandpal-green text-white'
                      : completedSteps.includes(index)
                        ? 'border-green-600 bg-green-600 text-white'
                        : 'border-gray-300'
                  }`}>
                    {completedSteps.includes(index) ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="hidden md:block">
                    <div className="font-medium">{step.title}</div>
                    <div className="text-xs text-gray-500">{step.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardHeader>
      </Card>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Waitlist Status Toggle for authenticated users */}
          {user && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {waitlistActive ? (
                      <BellRing className="h-5 w-5 text-grandpal-green" />
                    ) : (
                      <BellOff className="h-5 w-5 text-gray-400" />
                    )}
                    <span className="font-medium">
                      Waitlist {waitlistActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <Switch
                    checked={waitlistActive}
                    onCheckedChange={handleWaitlistToggle}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step Content */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="bg-grandpal-green text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
                  {currentStep + 1}
                </span>
                {STEPS[currentStep].title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {renderStepContent()}
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handlePrevStep}
              disabled={currentStep === 0}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>

            {currentStep === STEPS.length - 1 ? (
              <Button 
                type="submit" 
                disabled={isSubmitting || !validateStep(currentStep)}
                className="bg-grandpal-green hover:bg-grandpal-green-dark flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : user ? (
                  "Submit Waitlist Registration"
                ) : (
                  "Continue to Create Account"
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleNextStep}
                className="bg-grandpal-green hover:bg-grandpal-green-dark flex items-center gap-2"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}